import { agentCategories, agentListings, marketplaceFeed, promoSpots } from './mockData';
import { supabase, isSupabaseConfigured } from '../supabaseClient';

const sampleDataMode = (process.env.REACT_APP_SAMPLE_DATA_MODE || 'off').toLowerCase();
const placeholderMetricsEnabled =
  (process.env.REACT_APP_USE_PLACEHOLDER_METRICS || 'false').toLowerCase() === 'true';
const LISTING_COVER_BUCKET = 'agent-covers';
const ORDER_FILE_BUCKET = 'order-files';
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const orderFileContentTypes = {
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  gif: 'image/gif',
  heic: 'image/heic',
  heif: 'image/heif',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  pdf: 'application/pdf',
  png: 'image/png',
  txt: 'text/plain',
  webp: 'image/webp',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

function getTrustedUserId(currentUser) {
  if (!currentUser?.id) {
    return null;
  }

  const userId = String(currentUser.id);
  if (!uuidPattern.test(userId)) {
    throw new Error('Sign in again before continuing.');
  }

  return userId;
}

function getOrderFileContentType(file) {
  const extension = String(file?.name || '')
    .split('.')
    .pop()
    ?.toLowerCase();

  if (!extension || !orderFileContentTypes[extension]) {
    throw new Error('Unsupported file type.');
  }

  return orderFileContentTypes[extension];
}

function normalizeObject(value) {
  if (!value) {
    return {};
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  return typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function getPlaceholderHeroStats() {
  return [
    {
      label: 'Verified agents',
      value: process.env.REACT_APP_HERO_METRIC_VERIFIED_AGENTS || '7+',
    },
    {
      label: 'Tasks completed',
      value: process.env.REACT_APP_HERO_METRIC_TASKS_COMPLETED || '128+',
    },
    {
      label: 'Time saved',
      value: process.env.REACT_APP_HERO_METRIC_TIME_SAVED || '340+ hrs',
    },
  ];
}

function buildComputedHeroStats(listings) {
  const publishedListings = listings.filter(isPublicListing);
  const verifiedAgents = publishedListings.filter((listing) => listing.verified).length;
  const tasksCompleted = publishedListings.reduce(
    (sum, listing) => sum + toNumberOrFallback(listing.ordersCompleted),
    0
  );
  const hoursPerTask = toNumberOrFallback(process.env.REACT_APP_HERO_HOURS_PER_TASK, 1.4);
  const timeSavedHours = Math.round(tasksCompleted * hoursPerTask);

  return [
    { label: 'Verified agents', value: `${verifiedAgents}+` },
    { label: 'Tasks completed', value: `${tasksCompleted}+` },
    { label: 'Time saved', value: `${timeSavedHours}+ hrs` },
  ];
}

function mapMarketplaceMetrics(row) {
  return [
    { label: 'Verified agents', value: `${toNumberOrFallback(row.verified_agents)}+` },
    { label: 'Tasks completed', value: `${toNumberOrFallback(row.tasks_completed)}+` },
    { label: 'Time saved', value: `${toNumberOrFallback(row.time_saved_hours)}+ hrs` },
  ];
}

function mergeCategories(sampleCategories, liveCategories) {
  const categoriesBySlug = new Map();

  sampleCategories.forEach((category) => {
    categoriesBySlug.set(category.slug, {
      ...category,
      legacyIds: [category.id],
    });
  });

  liveCategories.forEach((category) => {
    const existing = categoriesBySlug.get(category.slug);

    if (existing) {
      categoriesBySlug.set(category.slug, {
        ...existing,
        ...category,
        legacyIds: Array.from(new Set([...(existing.legacyIds || []), existing.id, category.id])),
      });
      return;
    }

    categoriesBySlug.set(category.slug, {
      ...category,
      legacyIds: [category.id],
    });
  });

  return Array.from(categoriesBySlug.values()).sort((left, right) => {
    const leftOrder = left.sort_order ?? left.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = right.sort_order ?? right.sortOrder ?? Number.MAX_SAFE_INTEGER;
    return leftOrder - rightOrder || left.name.localeCompare(right.name);
  });
}

function buildSnapshot(listingsInput, categoriesInput = agentCategories, statsOverride = null) {
  const categories = categoriesInput.length > 0 ? categoriesInput : agentCategories;
  const listings = listingsInput
    .map((listing) => ({
      ...listing,
      category:
        listing.category ||
        categories.find(
          (category) =>
            category.id === listing.categoryId || category.legacyIds?.includes(listing.categoryId)
        ),
    }))
    .sort(
      (left, right) => compareListingDisplayPriority(left, right) || new Date(right.createdAt) - new Date(left.createdAt)
    );
  const publicListings = listings.filter(isPublicListing);
  const featuredListings = publicListings.filter((listing) => listing.featured).slice(0, 3);
  const featuredListingIds = new Set(featuredListings.map((listing) => listing.id));
  const featuredFallbackListings = publicListings
    .filter((listing) => !featuredListingIds.has(listing.id))
    .slice(0, Math.max(0, 3 - featuredListings.length));

  return {
    categories,
    listings,
    featuredListings: [...featuredListings, ...featuredFallbackListings],
    recentlyListed: publicListings.slice(0, 6),
    trendingListings: [...publicListings]
      .sort((left, right) => right.ordersCompleted - left.ordersCompleted)
      .slice(0, 4),
    topRatedListings: [...publicListings]
      .sort((left, right) => right.rating - left.rating || right.reviewCount - left.reviewCount)
      .slice(0, 4),
    marketplaceFeed,
    promoSpots,
    stats: statsOverride || buildComputedHeroStats(listings),
  };
}

function isPublicListing(listing) {
  const status = listing.status || 'published';
  const visibility = listing.visibility || 'public';
  return status === 'published' && visibility === 'public';
}

export function compareListingDisplayPriority(left, right) {
  return Number(Boolean(left?.isSampleListing)) - Number(Boolean(right?.isSampleListing));
}

export function mergeListings(sampleListings, liveListings) {
  const liveListingSlugs = new Set(liveListings.map((listing) => listing.slug));
  const liveEntries = liveListings.map((listing) => ({
    ...listing,
    isSampleListing: false,
  }));
  const sampleEntries = sampleListings
    .filter((listing) => !liveListingSlugs.has(listing.slug))
    .map((listing) => ({
      ...listing,
      isSampleListing: true,
    }));

  return [...liveEntries, ...sampleEntries];
}

function applySampleDataMode(liveListings) {
  if (sampleDataMode === 'merge') {
    return mergeListings(agentListings, liveListings);
  }

  if (sampleDataMode === 'fallback' && liveListings.length === 0) {
    return agentListings;
  }

  return liveListings;
}

function toNumberOrFallback(value, fallback = 0) {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

async function getFunctionErrorMessage(error, fallback = 'Request failed.') {
  const response = error?.context;

  if (response && typeof response.clone === 'function') {
    try {
      const body = await response.clone().json();
      if (body?.error) {
        return body.error;
      }
    } catch {
      return error?.message || fallback;
    }
  }

  return error?.message || fallback;
}

async function getFunctionAuthHeaders() {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (error) {
    throw error;
  }

  if (!session?.access_token) {
    throw new Error('Sign in again before continuing.');
  }

  return {
    Authorization: `Bearer ${session.access_token}`,
  };
}

function buildListingSlug(title, fallback = 'agent') {
  const base = String(title || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);

  return `${base || fallback}-${Math.random().toString(36).slice(2, 8)}`;
}

function mapPackages(packages = []) {
  return packages
    .sort((left, right) => left.sort_order - right.sort_order)
    .map((pkg) => ({
      tier: pkg.tier,
      name: pkg.name,
      description: pkg.description,
      price: pkg.price,
      deliveryDays: pkg.delivery_days,
      revisions: pkg.revisions,
      pricingType: pkg.pricing_type || 'flat_price',
      currency: pkg.currency_code || 'USD',
      billingInterval: pkg.billing_interval || null,
      pricingLabel: pkg.pricing_label || null,
      includedUsage: pkg.included_usage || {},
      overage: {
        enabled: pkg.overage_enabled || false,
        unit: pkg.overage_unit || null,
        amountPerUnit: pkg.overage_amount_per_unit || null,
      },
      features: pkg.features || [],
    }));
}

function mapFaqs(faqs = []) {
  return faqs
    .sort((left, right) => left.sort_order - right.sort_order)
    .map((faq) => ({
      question: faq.question,
      answer: faq.answer,
    }));
}

function mapBuyerInputs(inputs = []) {
  return inputs
    .sort((left, right) => {
      const groupOrder = left.input_group.localeCompare(right.input_group);
      return groupOrder || left.sort_order - right.sort_order;
    })
    .reduce(
      (accumulator, item) => {
        accumulator[item.input_group].push({
          name: item.name,
          type: item.input_type,
          acceptedFormats: item.accepted_formats || [],
          description: item.description || '',
        });
        return accumulator;
      },
      { required: [], optional: [] }
    );
}

function mapDeliverables(deliverables = []) {
  return deliverables
    .sort((left, right) => left.sort_order - right.sort_order)
    .map((item) => ({
      name: item.name,
      format: item.format || '',
      deliveryMethod: item.delivery_method || 'seller_managed',
      description: item.description || '',
    }));
}

function mapWorkflowSteps(steps = []) {
  return steps
    .sort((left, right) => left.sort_order - right.sort_order)
    .map((step) => step.step_text);
}

function mapGalleryAssets(assets = []) {
  return assets
    .sort((left, right) => left.sort_order - right.sort_order)
    .map((asset) => ({
      url: asset.asset_url,
      altText: asset.alt_text || '',
    }));
}

function mapReview(review) {
  return {
    id: review.id,
    orderId: review.order_id || null,
    reviewerName: review.reviewer_name,
    company: review.reviewer_company,
    rating: review.rating,
    headline: review.headline || '',
    body: review.body,
    createdAt: review.created_at || null,
  };
}

function mapOrderMessages(messages = []) {
  return messages
    .sort((left, right) => new Date(left.created_at) - new Date(right.created_at))
    .map((message) => ({
      id: message.id,
      senderId: message.sender_id,
      senderRole: message.sender_role,
      body: message.body,
      createdAt: message.created_at,
    }));
}

function mapOrderFiles(files = []) {
  return files
    .sort((left, right) => new Date(right.created_at) - new Date(left.created_at))
    .map((file) => ({
      id: file.id,
      uploaderId: file.uploader_id,
      uploaderRole: file.uploader_role,
      fileKind: file.file_kind,
      fileName: file.file_name,
      storagePath: file.storage_path,
      mimeType: file.mime_type || '',
      fileSizeBytes: file.file_size_bytes || 0,
      createdAt: file.created_at,
    }));
}

function mapOrder(row, currentUserId) {
  const role = row.seller_id === currentUserId ? 'seller' : 'buyer';
  const counterparty =
    role === 'seller'
      ? row.buyer
        ? {
            id: row.buyer.id,
            name: row.buyer.full_name,
            username: row.buyer.username || '',
            email: row.buyer.marketplace_email || '',
          }
        : null
      : row.seller
      ? {
          id: row.seller.id,
          name: row.seller.full_name,
          username: row.seller.username || '',
          email: row.seller.marketplace_email || '',
        }
      : null;

  const review = row.reviews?.[0] ? mapReview(row.reviews[0]) : null;

  return {
    id: row.id,
    listingId: row.listing_id,
    sellerId: row.seller_id,
    buyerId: row.buyer_id,
    role,
    status: row.status,
    pricingType: row.pricing_type || 'flat_price',
    billingInterval: row.billing_interval || null,
    currencyCode: row.currency_code || 'USD',
    totalAmount: toNumberOrFallback(row.amount),
    buyerName: row.buyer_name,
    buyerEmail: row.buyer_email,
    buyerCompany: row.buyer_company || '',
    projectBrief: row.project_brief || '',
    sellerNotes: row.seller_notes || '',
    dueDate: row.due_at,
    deliveredAt: row.delivered_at,
    completedAt: row.completed_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    listing: row.listing
      ? {
          id: row.listing.id,
          slug: row.listing.slug,
          title: row.listing.title,
          pricingType: row.listing.pricing_type || 'flat_price',
          billingInterval: row.listing.billing_interval || null,
          category: row.listing.category || null,
        }
      : null,
    seller: row.seller
      ? {
          id: row.seller.id,
          name: row.seller.full_name,
          username: row.seller.username || '',
          headline: row.seller.headline || '',
          marketplaceEmail: row.seller.marketplace_email || '',
        }
      : null,
    buyer: row.buyer
      ? {
          id: row.buyer.id,
          name: row.buyer.full_name,
          username: row.buyer.username || '',
          marketplaceEmail: row.buyer.marketplace_email || '',
        }
      : null,
    counterparty,
    messages: mapOrderMessages(row.messages || []),
    files: mapOrderFiles(row.files || []),
    review,
    canLeaveReview: role === 'buyer' && row.status === 'completed' && !review,
  };
}

async function uploadListingCover(file, userId, slug) {
  if (!file) {
    return null;
  }

  const extension = file.name?.split('.').pop()?.toLowerCase() || 'jpg';
  const path = `${userId}/${slug}-${Date.now()}.${extension}`;
  const { error: uploadError } = await supabase.storage
    .from(LISTING_COVER_BUCKET)
    .upload(path, file, {
      cacheControl: '3600',
      upsert: true,
    });

  if (uploadError) {
    throw uploadError;
  }

  const { data } = supabase.storage.from(LISTING_COVER_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

async function uploadOrderFileToStorage(file, userId, orderId) {
  if (!file) {
    return null;
  }

  const safeName = String(file.name || 'attachment')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-');
  const path = `${userId}/${orderId}/${Date.now()}-${safeName}`;
  const { error: uploadError } = await supabase.storage
    .from(ORDER_FILE_BUCKET)
    .upload(path, file, {
      cacheControl: '3600',
      contentType: getOrderFileContentType(file),
      upsert: false,
    });

  if (uploadError) {
    throw uploadError;
  }

  return path;
}

function mapListing(row) {
  const pricingAmount = row.pricing_amount ?? row.price_from ?? 0;
  const buyerInputs = mapBuyerInputs(row.buyer_inputs || []);
  const reviewRows = row.reviews || [];
  const reviewCount = reviewRows.length || row.review_count || 0;
  const reviewRatingAverage =
    reviewRows.length > 0
      ? reviewRows.reduce((sum, review) => sum + toNumberOrFallback(review.rating), 0) / reviewRows.length
      : toNumberOrFallback(row.rating_average);

  return {
    id: row.id,
    slug: row.slug,
    title: row.title,
    tagline: row.tagline,
    summary: row.summary,
    description: row.description,
    status: row.status || 'draft',
    categoryId: row.category_id,
    category: row.category || null,
    priceFrom: toNumberOrFallback(pricingAmount),
    pricingType: row.pricing_type || 'flat_price',
    pricingCurrency: row.pricing_currency || 'USD',
    pricingAmount: toNumberOrFallback(pricingAmount),
    pricingLabel: row.pricing_label || null,
    pricingDescription: row.pricing_description || null,
    billingInterval: row.billing_interval || null,
    includedUsage: normalizeObject(row.included_usage),
    overage: {
      enabled: row.overage_enabled || false,
      unit: row.overage_unit || null,
      amountPerUnit: row.overage_amount_per_unit || null,
    },
    visibility: row.visibility || 'public',
    deliveryDays: row.delivery_days,
    revisionCount: row.revision_count,
    featured: row.featured,
    verified: row.verified,
    instantHire: row.instant_hire,
    createdAt: row.created_at,
    responseHours: row.response_time_hours,
    ordersCompleted: row.orders_completed,
    successRate: row.success_rate,
    rating: reviewRatingAverage,
    reviewCount,
    agentType: row.agent_type || null,
    automationLevel: row.automation_level || null,
    stack: row.stack || [],
    toolsUsed: row.tools_used || [],
    integrations: row.integrations || [],
    industries: row.industries || [],
    bestFor: row.best_for || [],
    notFor: row.not_for || [],
    outcomes: row.outcomes || [],
    useCases: row.use_cases || [],
    outputFormats: row.output_formats || [],
    thumbnailUrl: row.thumbnail_url || null,
    demoVideoUrl: row.demo_video_url || null,
    sampleOutput: {
      enabled: row.sample_output_enabled || false,
      url: row.sample_output_url || null,
      description: row.sample_output_description || null,
    },
    dataPolicy: {
      storesBuyerData: row.stores_buyer_data || false,
      retentionDays: row.retention_days,
      usesBuyerDataForTraining: row.uses_buyer_data_for_training || false,
      requiresCredentials: row.requires_credentials || false,
      sensitiveDataAllowed: row.sensitive_data_allowed || false,
      thirdPartyToolsUsed: row.third_party_tools_used || false,
    },
    requiresBuyerReview: row.requires_buyer_review ?? true,
    prohibitedUses: row.prohibited_uses || [],
    limitations: row.limitations || [],
    featuredQuote: row.featured_quote,
    tone: row.cover_tone || 'graphite',
    seller: row.seller
      ? {
          id: row.seller.id,
          name: row.seller.full_name,
          username: row.seller.username,
          headline: row.seller.headline,
          location: row.seller.location,
          memberSince: row.seller.created_at,
          responseRate: row.seller.response_rate,
          completionRate: row.seller.completion_rate,
          about: row.seller.bio,
          sellerType: row.seller.seller_type || 'individual',
          verified: row.seller.verified || false,
          stripeAccountId: row.seller.stripe_account_id || '',
          stripeOnboardingComplete: row.seller.stripe_onboarding_complete || false,
          stripePayoutsEnabled: row.seller.stripe_payouts_enabled || false,
        }
      : null,
    packages: mapPackages(row.packages || []),
    buyerInputs,
    deliverables: mapDeliverables(row.deliverables || []),
    workflow: mapWorkflowSteps(row.workflow_steps || []),
    gallery: mapGalleryAssets(row.gallery_assets || []),
    faqs: mapFaqs(row.faqs || []),
    reviews: (row.reviews || []).map(mapReview),
  };
}

function buildFallbackProfile(user) {
  return {
    id: user?.id || 'sample-user',
    fullName: user?.user_metadata?.full_name || user?.email?.split('@')[0] || '',
    marketplaceEmail: user?.email || '',
    username: '',
    headline: '',
    bio: '',
    stripeAccountId: '',
    stripeOnboardingComplete: false,
    stripePayoutsEnabled: false,
    responseRate: 100,
    completionRate: 100,
  };
}

function mapProfile(row, user) {
  if (!row) {
    return buildFallbackProfile(user);
  }

  return {
    id: row.id,
    fullName: row.full_name || user?.user_metadata?.full_name || '',
    marketplaceEmail: row.marketplace_email || user?.email || '',
    username: row.username || '',
    headline: row.headline || '',
    bio: row.bio || '',
    stripeAccountId: row.stripe_account_id || '',
    stripeOnboardingComplete: row.stripe_onboarding_complete || false,
    stripePayoutsEnabled: row.stripe_payouts_enabled || false,
    responseRate: row.response_rate ?? 100,
    completionRate: row.completion_rate ?? 100,
  };
}

function buildListingRows(payload, listingId = null) {
  const faqRows = (payload.faqs || []).map((faq, index) => ({
    ...(listingId ? { listing_id: listingId } : {}),
    question: faq.question,
    answer: faq.answer,
    sort_order: index,
  }));

  const buyerInputRows = [
    ...((payload.buyerInputs?.required || []).map((item, index) => ({
      ...(listingId ? { listing_id: listingId } : {}),
      input_group: 'required',
      name: item.name,
      input_type: item.type,
      accepted_formats: item.acceptedFormats || [],
      description: item.description || null,
      sort_order: index,
    })) || []),
    ...((payload.buyerInputs?.optional || []).map((item, index) => ({
      ...(listingId ? { listing_id: listingId } : {}),
      input_group: 'optional',
      name: item.name,
      input_type: item.type,
      accepted_formats: item.acceptedFormats || [],
      description: item.description || null,
      sort_order: index,
    })) || []),
  ];

  const deliverableRows = (payload.deliverables || []).map((item, index) => ({
    ...(listingId ? { listing_id: listingId } : {}),
    name: item.name,
    format: item.format || null,
    delivery_method: item.deliveryMethod || 'seller_managed',
    description: item.description || null,
    sort_order: index,
  }));

  const workflowRows = (payload.workflow || []).map((step, index) => ({
    ...(listingId ? { listing_id: listingId } : {}),
    step_text: step,
    sort_order: index,
  }));

  const galleryRows = (payload.media?.galleryUrls || []).map((url, index) => ({
    ...(listingId ? { listing_id: listingId } : {}),
    asset_url: url,
    sort_order: index,
  }));

  return {
    faqRows,
    buyerInputRows,
    deliverableRows,
    workflowRows,
    galleryRows,
  };
}

async function replaceListingRows(tableName, listingId, rows) {
  const { error: deleteError } = await supabase.from(tableName).delete().eq('listing_id', listingId);

  if (deleteError) {
    throw deleteError;
  }

  if (rows.length === 0) {
    return;
  }

  const { error: insertError } = await supabase.from(tableName).insert(rows);

  if (insertError) {
    throw insertError;
  }
}

async function loadSupabaseSnapshot(currentUser) {
  const currentUserId = getTrustedUserId(currentUser);
  const metricsPromise = placeholderMetricsEnabled
    ? Promise.resolve({ data: null, error: null })
    : supabase.from('marketplace_metrics').select('*').eq('key', 'default').maybeSingle();

  const ordersPromise =
    currentUserId
      ? supabase
          .from('platform_orders')
          .select(
            `
              *,
              listing:agent_listings(
                id,
                slug,
                title,
                pricing_type,
                billing_interval,
                category:agent_categories(*)
              ),
              seller:profiles!platform_orders_seller_id_fkey(
                id,
                full_name,
                username,
                headline,
                marketplace_email
              ),
              buyer:profiles!platform_orders_buyer_id_fkey(
                id,
                full_name,
                username,
                marketplace_email
              ),
              messages:order_messages(*),
              files:order_files(*),
              reviews:listing_reviews(*)
            `
          )
          .or(`buyer_id.eq.${currentUserId},seller_id.eq.${currentUserId}`)
          .order('created_at', { ascending: false })
      : Promise.resolve({ data: [], error: null });

  const [listingsResponse, categoriesResponse, ordersResponse, metricsResponse] = await Promise.all([
    (() => {
      const query = supabase
      .from('agent_listings')
      .select(
        `
          *,
          category:agent_categories(*),
          seller:profiles!agent_listings_seller_id_fkey(*),
          packages:listing_packages(*),
          faqs:listing_faqs(*),
          reviews:listing_reviews(*),
          buyer_inputs:listing_buyer_inputs(*),
          deliverables:listing_deliverables(*),
          workflow_steps:listing_workflow_steps(*),
          gallery_assets:listing_gallery_assets(*)
        `
      )
      .order('created_at', { ascending: false });

      if (currentUserId) {
        return query.or(`status.eq.published,seller_id.eq.${currentUserId}`);
      }

      return query.eq('status', 'published');
    })(),
    supabase.from('agent_categories').select('*').order('sort_order', { ascending: true }),
    ordersPromise,
    metricsPromise,
  ]);

  if (listingsResponse.error) {
    throw listingsResponse.error;
  }

  if (categoriesResponse.error) {
    throw categoriesResponse.error;
  }

  if (ordersResponse.error) {
    throw ordersResponse.error;
  }

  if (metricsResponse.error) {
    throw metricsResponse.error;
  }

  const mappedListings = listingsResponse.data.map(mapListing);
  const listings = applySampleDataMode(mappedListings);
  const categories =
    sampleDataMode === 'merge'
      ? mergeCategories(agentCategories, categoriesResponse.data || [])
      : (categoriesResponse.data || []);

  const stats = placeholderMetricsEnabled
    ? getPlaceholderHeroStats()
    : metricsResponse.data
    ? mapMarketplaceMetrics(metricsResponse.data)
    : buildComputedHeroStats(listings);

  return {
    ...buildSnapshot(listings, categories, stats),
    orders: (ordersResponse.data || []).map((order) => mapOrder(order, currentUser?.id)),
    source: sampleDataMode === 'merge' ? 'supabase+sample' : 'supabase',
  };
}

export async function getMarketplaceData(currentUser) {
  if (!isSupabaseConfigured || !supabase) {
    return {
      ...buildSnapshot(
        agentListings,
        agentCategories,
        placeholderMetricsEnabled ? getPlaceholderHeroStats() : buildComputedHeroStats(agentListings)
      ),
      orders: [],
      source: 'sample',
    };
  }

  try {
    return await loadSupabaseSnapshot(currentUser);
  } catch (error) {
    return {
      ...buildSnapshot(
        agentListings,
        agentCategories,
        placeholderMetricsEnabled ? getPlaceholderHeroStats() : buildComputedHeroStats(agentListings)
      ),
      orders: [],
      source: 'sample',
      sourceError: error.message,
    };
  }
}

export async function createListing(payload, userId) {
  if (!isSupabaseConfigured || !supabase) {
    return {
      mock: true,
      listing: {
        id: `draft-${Date.now()}`,
        ...payload,
      },
    };
  }

  const { data: sellerProfile, error: profileError } = await supabase
    .from('profiles')
    .select('stripe_account_id, stripe_payouts_enabled')
    .eq('id', userId)
    .single();

  if (profileError) {
    throw profileError;
  }

  if (!sellerProfile?.stripe_account_id) {
    throw new Error('Connect Stripe before submitting your agent.');
  }

  const listingSlug = payload.slug || buildListingSlug(payload.title);
  const thumbnailUrl =
    payload.media?.thumbnailFile && userId
      ? await uploadListingCover(payload.media.thumbnailFile, userId, listingSlug)
      : null;
  const listingStatus = sellerProfile.stripe_payouts_enabled ? 'published' : 'draft';

  const pricing = payload.pricing || {};
  const delivery = payload.delivery || {};
  const agent = payload.agent || {};
  const market = payload.market || {};
  const sampleOutput = payload.sampleOutput || {};
  const dataPolicy = payload.dataPolicy || {};
  const compliance = payload.compliance || {};

  const { data: listing, error: listingError } = await supabase
    .from('agent_listings')
    .insert({
      seller_id: userId,
      category_id: payload.categoryId,
      slug: listingSlug,
      title: payload.title,
      tagline: payload.tagline,
      summary: payload.summary,
      description: payload.description,
      price_from: toNumberOrFallback(pricing.amount),
      delivery_days: toNumberOrFallback(delivery.deliveryDays, 7),
      revision_count: toNumberOrFallback(delivery.revisionCount, 0),
      featured: false,
      verified: false,
      instant_hire: Boolean(delivery.instantHire),
      response_time_hours: toNumberOrFallback(delivery.responseHours, 24),
      orders_completed: 0,
      success_rate: 100,
      rating_average: 0,
      review_count: 0,
      visibility: payload.visibility || 'public',
      pricing_type: pricing.type || 'flat_price',
      pricing_currency: pricing.currency || 'USD',
      pricing_amount: toNumberOrFallback(pricing.amount),
      pricing_label: pricing.label,
      pricing_description: pricing.description,
      billing_interval: pricing.billingInterval,
      included_usage: pricing.includedUsage || {},
      overage_enabled: pricing.overage?.enabled || false,
      overage_unit: pricing.overage?.unit,
      overage_amount_per_unit: pricing.overage?.amountPerUnit,
      agent_type: agent.agentType,
      automation_level: agent.automationLevel,
      stack: agent.stack || [],
      tools_used: agent.toolsUsed || [],
      integrations: agent.integrations || [],
      industries: market.industries || [],
      best_for: market.bestFor || [],
      not_for: market.notFor || [],
      outcomes: market.outcomes || [],
      use_cases: market.useCases || [],
      output_formats: payload.outputFormats || [],
      sample_output_enabled: sampleOutput.enabled || false,
      sample_output_url: sampleOutput.url,
      sample_output_description: sampleOutput.description,
      stores_buyer_data: dataPolicy.storesBuyerData || false,
      retention_days: dataPolicy.retentionDays,
      uses_buyer_data_for_training: dataPolicy.usesBuyerDataForTraining || false,
      requires_credentials: dataPolicy.requiresCredentials || false,
      sensitive_data_allowed: dataPolicy.sensitiveDataAllowed || false,
      third_party_tools_used: dataPolicy.thirdPartyToolsUsed || false,
      requires_buyer_review: compliance.requiresBuyerReview ?? true,
      prohibited_uses: compliance.prohibitedUses || [],
      limitations: payload.limitations || [],
      thumbnail_url: thumbnailUrl,
      demo_video_url: payload.media?.demoVideoUrl || null,
      featured_quote: payload.featuredQuote,
      cover_tone: payload.tone,
      status: listingStatus,
    })
    .select()
    .single();

  if (listingError) {
    throw listingError;
  }

  const { faqRows, buyerInputRows, deliverableRows, workflowRows, galleryRows } = buildListingRows(
    payload,
    listing.id
  );

  if (faqRows.length > 0) {
    const { error: faqError } = await supabase.from('listing_faqs').insert(faqRows);

    if (faqError) {
      throw faqError;
    }
  }

  if (buyerInputRows.length > 0) {
    const { error: buyerInputsError } = await supabase
      .from('listing_buyer_inputs')
      .insert(buyerInputRows);

    if (buyerInputsError) {
      throw buyerInputsError;
    }
  }

  if (deliverableRows.length > 0) {
    const { error: deliverablesError } = await supabase
      .from('listing_deliverables')
      .insert(deliverableRows);

    if (deliverablesError) {
      throw deliverablesError;
    }
  }

  if (workflowRows.length > 0) {
    const { error: workflowError } = await supabase
      .from('listing_workflow_steps')
      .insert(workflowRows);

    if (workflowError) {
      throw workflowError;
    }
  }

  if (galleryRows.length > 0) {
    const { error: galleryError } = await supabase
      .from('listing_gallery_assets')
      .insert(galleryRows);

    if (galleryError) {
      throw galleryError;
    }
  }

  return {
    mock: false,
    listing,
  };
}

export async function updateListing(listingId, payload, userId) {
  if (!isSupabaseConfigured || !supabase) {
    return {
      mock: true,
      listing: {
        id: listingId,
        ...payload,
      },
    };
  }

  const { data: existingListing, error: existingError } = await supabase
    .from('agent_listings')
    .select('id, seller_id, slug, thumbnail_url')
    .eq('id', listingId)
    .single();

  if (existingError) {
    throw existingError;
  }

  if (existingListing.seller_id !== userId) {
    throw new Error('You can only edit listings you own.');
  }

  const { data: sellerProfile, error: profileError } = await supabase
    .from('profiles')
    .select('stripe_payouts_enabled')
    .eq('id', userId)
    .single();

  if (profileError) {
    throw profileError;
  }

  const listingSlug = payload.slug || existingListing.slug || buildListingSlug(payload.title);
  const thumbnailUrl =
    payload.media?.thumbnailFile && userId
      ? await uploadListingCover(payload.media.thumbnailFile, userId, listingSlug)
      : payload.media?.coverMode === 'upload'
        ? existingListing.thumbnail_url
        : null;
  const pricing = payload.pricing || {};
  const delivery = payload.delivery || {};
  const agent = payload.agent || {};
  const market = payload.market || {};
  const sampleOutput = payload.sampleOutput || {};
  const dataPolicy = payload.dataPolicy || {};
  const compliance = payload.compliance || {};

  const { data: listing, error: listingError } = await supabase
    .from('agent_listings')
    .update({
      category_id: payload.categoryId,
      slug: listingSlug,
      title: payload.title,
      tagline: payload.tagline,
      summary: payload.summary,
      description: payload.description,
      price_from: toNumberOrFallback(pricing.amount),
      delivery_days: toNumberOrFallback(delivery.deliveryDays, 7),
      revision_count: toNumberOrFallback(delivery.revisionCount, 0),
      instant_hire: Boolean(delivery.instantHire),
      response_time_hours: toNumberOrFallback(delivery.responseHours, 24),
      visibility: payload.visibility || 'public',
      pricing_type: pricing.type || 'flat_price',
      pricing_currency: pricing.currency || 'USD',
      pricing_amount: toNumberOrFallback(pricing.amount),
      pricing_label: pricing.label,
      pricing_description: pricing.description,
      billing_interval: pricing.billingInterval,
      included_usage: pricing.includedUsage || {},
      overage_enabled: pricing.overage?.enabled || false,
      overage_unit: pricing.overage?.unit,
      overage_amount_per_unit: pricing.overage?.amountPerUnit,
      agent_type: agent.agentType,
      automation_level: agent.automationLevel,
      stack: agent.stack || [],
      tools_used: agent.toolsUsed || [],
      integrations: agent.integrations || [],
      industries: market.industries || [],
      best_for: market.bestFor || [],
      not_for: market.notFor || [],
      outcomes: market.outcomes || [],
      use_cases: market.useCases || [],
      output_formats: payload.outputFormats || [],
      sample_output_enabled: sampleOutput.enabled || false,
      sample_output_url: sampleOutput.url,
      sample_output_description: sampleOutput.description,
      stores_buyer_data: dataPolicy.storesBuyerData || false,
      retention_days: dataPolicy.retentionDays,
      uses_buyer_data_for_training: dataPolicy.usesBuyerDataForTraining || false,
      requires_credentials: dataPolicy.requiresCredentials || false,
      sensitive_data_allowed: dataPolicy.sensitiveDataAllowed || false,
      third_party_tools_used: dataPolicy.thirdPartyToolsUsed || false,
      requires_buyer_review: compliance.requiresBuyerReview ?? true,
      prohibited_uses: compliance.prohibitedUses || [],
      limitations: payload.limitations || [],
      thumbnail_url: thumbnailUrl,
      demo_video_url: payload.media?.demoVideoUrl || null,
      featured_quote: payload.featuredQuote,
      cover_tone: payload.tone,
      status: sellerProfile.stripe_payouts_enabled ? 'published' : 'draft',
    })
    .eq('id', listingId)
    .eq('seller_id', userId)
    .select()
    .single();

  if (listingError) {
    throw listingError;
  }

  const { faqRows, buyerInputRows, deliverableRows, workflowRows, galleryRows } = buildListingRows(
    payload,
    listing.id
  );

  await replaceListingRows('listing_faqs', listing.id, faqRows);
  await replaceListingRows('listing_buyer_inputs', listing.id, buyerInputRows);
  await replaceListingRows('listing_deliverables', listing.id, deliverableRows);
  await replaceListingRows('listing_workflow_steps', listing.id, workflowRows);
  await replaceListingRows('listing_gallery_assets', listing.id, galleryRows);

  return {
    mock: false,
    listing,
  };
}

export async function createOrder(payload, currentUser) {
  if (!currentUser?.id) {
    throw new Error('Sign in to place an order.');
  }

  if (!isSupabaseConfigured || !supabase) {
    return {
      mock: true,
      order: {
        id: `order-${Date.now()}`,
        listingId: payload.listingId,
      },
    };
  }

  const { data, error } = await supabase.functions.invoke('create-checkout-session', {
    headers: await getFunctionAuthHeaders(),
    body: {
      listingId: payload.listingId,
      name: payload.name,
      email: payload.email,
      company: payload.company,
      brief: payload.brief,
      origin: window.location.origin,
    },
  });

  if (error) {
    throw new Error(await getFunctionErrorMessage(error, 'Unable to start checkout.'));
  }

  return {
    mock: false,
    order: data.order,
    checkoutUrl: data.checkoutUrl,
  };
}

export async function createStripeConnectLink(currentUser, returnPath = '/account') {
  if (!currentUser?.id) {
    throw new Error('Sign in to connect Stripe payouts.');
  }

  if (!isSupabaseConfigured || !supabase) {
    return {
      mock: true,
      url: '#',
    };
  }

  const { data, error } = await supabase.functions.invoke('stripe-connect-link', {
    headers: await getFunctionAuthHeaders(),
    body: {
      origin: window.location.origin,
      returnPath,
    },
  });

  if (error) {
    throw new Error(await getFunctionErrorMessage(error, 'Unable to open Stripe.'));
  }

  return {
    mock: false,
    url: data.url,
    mode: data.mode,
  };
}

export async function getOrderDetail(orderId, currentUser) {
  if (!currentUser?.id) {
    throw new Error('Sign in to view order details.');
  }

  if (!isSupabaseConfigured || !supabase) {
    return {
      mock: true,
      order: null,
    };
  }

  const { data, error } = await supabase
    .from('platform_orders')
    .select(
      `
        *,
        listing:agent_listings(
          id,
          slug,
          title,
          pricing_type,
          billing_interval,
          category:agent_categories(*)
        ),
        seller:profiles!platform_orders_seller_id_fkey(
          id,
          full_name,
          username,
          headline,
          marketplace_email
        ),
        buyer:profiles!platform_orders_buyer_id_fkey(
          id,
          full_name,
          username,
          marketplace_email
        ),
        messages:order_messages(*),
        files:order_files(*),
        reviews:listing_reviews(*)
      `
    )
    .eq('id', orderId)
    .single();

  if (error) {
    throw error;
  }

  return {
    mock: false,
    order: mapOrder(data, currentUser.id),
  };
}

export async function sendOrderMessage(orderId, currentUser, body) {
  if (!currentUser?.id) {
    throw new Error('Sign in to send a message.');
  }

  const trimmedBody = body.trim();
  if (!trimmedBody) {
    throw new Error('Message cannot be empty.');
  }

  if (!isSupabaseConfigured || !supabase) {
    return {
      mock: true,
    };
  }

  const { data: order, error: orderError } = await supabase
    .from('platform_orders')
    .select('id, buyer_id, seller_id')
    .eq('id', orderId)
    .single();

  if (orderError) {
    throw orderError;
  }

  const senderRole = order.seller_id === currentUser.id ? 'seller' : 'buyer';
  const { error } = await supabase.from('order_messages').insert({
    order_id: orderId,
    sender_id: currentUser.id,
    sender_role: senderRole,
    body: trimmedBody,
  });

  if (error) {
    throw error;
  }

  return {
    mock: false,
  };
}

export async function uploadOrderFile(orderId, currentUser, file, fileKind = 'attachment') {
  if (!currentUser?.id) {
    throw new Error('Sign in to upload files.');
  }

  if (!file) {
    throw new Error('Choose a file to upload.');
  }

  if (!isSupabaseConfigured || !supabase) {
    return {
      mock: true,
    };
  }

  const { data: order, error: orderError } = await supabase
    .from('platform_orders')
    .select('id, buyer_id, seller_id')
    .eq('id', orderId)
    .single();

  if (orderError) {
    throw orderError;
  }

  const uploaderRole =
    order.seller_id === currentUser.id ? 'seller' : order.buyer_id === currentUser.id ? 'buyer' : null;

  if (!uploaderRole) {
    throw new Error('Only the buyer or seller on this order can upload files.');
  }

  const storagePath = await uploadOrderFileToStorage(file, currentUser.id, orderId);

  const { error } = await supabase.from('order_files').insert({
    order_id: orderId,
    uploader_id: currentUser.id,
    uploader_role: uploaderRole,
    file_kind: fileKind,
    file_name: file.name,
    storage_path: storagePath,
    mime_type: file.type || null,
    file_size_bytes: file.size || null,
  });

  if (error) {
    throw error;
  }

  return {
    mock: false,
  };
}

export async function getOrderFileSignedUrl(storagePath) {
  if (!isSupabaseConfigured || !supabase) {
    return {
      mock: true,
      url: '#',
    };
  }

  const { data, error } = await supabase.storage.from(ORDER_FILE_BUCKET).createSignedUrl(storagePath, 60);

  if (error) {
    throw error;
  }

  return {
    mock: false,
    url: data.signedUrl,
  };
}

export async function updateOrderStatus(orderId, currentUser, nextStatus) {
  if (!currentUser?.id) {
    throw new Error('Sign in to manage order status.');
  }

  if (!isSupabaseConfigured || !supabase) {
    return {
      mock: true,
    };
  }

  const patch = { status: nextStatus };
  if (nextStatus === 'delivered') {
    patch.delivered_at = new Date().toISOString();
  }
  if (nextStatus === 'completed') {
    patch.completed_at = new Date().toISOString();
  }

  const { error } = await supabase.from('platform_orders').update(patch).eq('id', orderId);

  if (error) {
    throw error;
  }

  return {
    mock: false,
  };
}

export async function submitOrderReview(orderId, currentUser, payload) {
  if (!currentUser?.id) {
    throw new Error('Sign in to leave a review.');
  }

  if (!isSupabaseConfigured || !supabase) {
    return {
      mock: true,
    };
  }

  const { data: order, error: orderError } = await supabase
    .from('platform_orders')
    .select('id, listing_id, buyer_id, buyer_name, buyer_company, status')
    .eq('id', orderId)
    .single();

  if (orderError) {
    throw orderError;
  }

  if (order.buyer_id !== currentUser.id || order.status !== 'completed') {
    throw new Error('Only completed buyer orders can be reviewed.');
  }

  const { error } = await supabase.from('listing_reviews').insert({
    order_id: order.id,
    listing_id: order.listing_id,
    buyer_id: currentUser.id,
    reviewer_name: order.buyer_name,
    reviewer_company: order.buyer_company || null,
    rating: payload.rating,
    headline: payload.headline || null,
    body: payload.body,
  });

  if (error) {
    throw error;
  }

  return {
    mock: false,
  };
}

export async function getAccountProfile(user) {
  if (!user) {
    return null;
  }

  if (!isSupabaseConfigured || !supabase) {
    return {
      mock: true,
      profile: buildFallbackProfile(user),
    };
  }

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .maybeSingle();

  if (error) {
    throw error;
  }

  let profileRow = data;
  if (data?.stripe_account_id && !data.stripe_payouts_enabled) {
    const { data: syncedData, error: syncError } = await supabase.functions.invoke('stripe-connect-link', {
      headers: await getFunctionAuthHeaders(),
      body: {
        statusOnly: true,
      },
    });

    if (!syncError && syncedData?.profile) {
      profileRow = {
        ...data,
        ...syncedData.profile,
      };
    }
  }

  return {
    mock: false,
    profile: mapProfile(profileRow, user),
  };
}

export async function updateAccountProfile(userId, payload, user) {
  if (!isSupabaseConfigured || !supabase) {
    return {
      mock: true,
      profile: {
        ...buildFallbackProfile(user),
        ...payload,
      },
    };
  }

  const username = String(payload.username || '')
    .trim()
    .replace(/\s+/g, '')
    .replace(/^@+/, '');

  const { data, error } = await supabase
    .from('profiles')
    .upsert(
      {
        id: userId,
        full_name: String(payload.fullName || '').trim() || null,
        marketplace_email: String(payload.marketplaceEmail || '').trim() || user?.email || null,
        username: username || null,
        headline: String(payload.headline || '').trim() || null,
        bio: String(payload.bio || '').trim() || null,
      },
      {
        onConflict: 'id',
      }
    )
    .select('*')
    .single();

  if (error) {
    throw error;
  }

  return {
    mock: false,
    profile: mapProfile(data, user),
  };
}

export async function deleteListing(listingId) {
  if (!isSupabaseConfigured || !supabase) {
    return {
      mock: true,
    };
  }

  const { error } = await supabase.from('agent_listings').delete().eq('id', listingId);

  if (error) {
    throw error;
  }

  return {
    mock: false,
  };
}

export async function submitBuyerRequest(payload) {
  if (!isSupabaseConfigured || !supabase) {
    return {
      mock: true,
    };
  }

  const { error } = await supabase.from('buyer_requests').insert({
    listing_id: payload.listingId,
    buyer_name: payload.name,
    buyer_email: payload.email,
    company_name: payload.company,
    budget_label: payload.budget,
    project_brief: payload.brief,
  });

  if (error) {
    throw error;
  }

  return {
    mock: false,
  };
}
